import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLists } from '../../context/ListsContext';
import { useBookmarks } from '../../context/BookmarksContext';
import { useSettings } from '../../context/SettingsContext';
import { FEED_DISPLAY_CONFIG, getDefaultEnabledFeedKeys } from '../../config/feed-details';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import CreatePostModal from '../CreatePostModal/CreatePostModal';
import CreateListModal from '../CreateListModal/CreateListModal';
import './Sidebar.css';

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { lists } = useLists();
  const { startFeedPath } = useSettings();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showArticleSubmenu, setShowArticleSubmenu] = useState(false);
  const [showFlexSubmenu, setShowFlexSubmenu] = useState(false);
  const [showFlexLists, setShowFlexLists] = useState(false);
  const [showListsSubmenu, setShowListsSubmenu] = useState(false);
  const [showBookmarksSubmenu, setShowBookmarksSubmenu] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const { folders } = useBookmarks();
  const [enabledFeeds, setEnabledFeeds] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useBodyScrollLock(expanded);
  const createMenuRef = useRef(null);
  const flexListsRef = useRef(null);

  // Fetch enabled feeds for Flex submenu
  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        setEnabledFeeds(
          feeds
            .filter(f => f.enabled !== false && FEED_DISPLAY_CONFIG[f.key]?.enabledByDefault)
            .map(f => ({ key: f.key, label: FEED_DISPLAY_CONFIG[f.key]?.label || f.label }))
        );
      } catch {
        setEnabledFeeds(
          getDefaultEnabledFeedKeys().map(key => ({
            key,
            label: FEED_DISPLAY_CONFIG[key]?.label || key,
          }))
        );
      }
    };
    fetchFeeds();
  }, []);

  // Lists with at least one member
  const listsWithMembers = (lists || []).filter(l => l.members && l.members.length > 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createMenuRef.current && !createMenuRef.current.contains(event.target)) {
        setShowCreateMenu(false);
      }
    };

    if (showCreateMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateMenu]);

  // Handle mobile auto-close and separate lists accordion behavior
  useEffect(() => {
    // 1. Auto-close sidebar on mobile after navigating
    if (window.innerWidth <= 768) {
      if (typeof onClose === 'function') {
        onClose();
      }
      setShowFlexSubmenu(false);
      setShowFlexLists(false);
      setShowListsSubmenu(false);
      setShowBookmarksSubmenu(false);
      setShowArticleSubmenu(false);
    } else {
      // 2. Manage list submenus: keep active list open, close the other
      const isMainListActive = location.pathname.includes('/lists/');
      // Rettelse: Tilføjet trailing slash for ikke at åbne på det primære /bookmarks link
      const isBookmarksActive = location.pathname.includes('/bookmarks/'); 
      const isFlexListActive = (lists || []).some(
        (list) => location.pathname === `/feed/${encodeURIComponent(list.name)}`
      );

      if (isMainListActive) {
        setShowListsSubmenu(true);
        setShowBookmarksSubmenu(false);
        setShowFlexLists(false);
      } else if (isBookmarksActive) {
        setShowBookmarksSubmenu(true);
        setShowListsSubmenu(false);
        setShowFlexLists(false);
      } else if (isFlexListActive) {
        setShowFlexSubmenu(true);
        setShowFlexLists(true);
        setShowListsSubmenu(false);
        setShowBookmarksSubmenu(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/feed') {
      return location.pathname.startsWith('/feed/');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <aside className={`sidebar${expanded ? ' expanded' : ''}`}>
        <div className="sidebar-inner">
          <div className="sidebar-header">
            {/* Mobile close button — shown only at ≤768px */}
            <button
              className="sidebar-close-btn"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            {/* Expand/collapse toggle — shown at 769px–1024px */}
            <button
              className="sidebar-toggle-btn"
              onClick={() => setExpanded(v => !v)}
              aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {expanded ? (
                /* Collapse: two left-pointing chevrons */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="11 17 6 12 11 7" />
                  <polyline points="18 17 13 12 18 7" />
                </svg>
              ) : (
                /* Expand: two right-pointing chevrons */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="13 17 18 12 13 7" />
                  <polyline points="6 17 11 12 6 7" />
                </svg>
              )}
            </button>
          </div>
          <div className="sidebar-content">
            {/* Main Navigation */}
            <nav className="sidebar-nav">
              <Link
                to={startFeedPath || '/'}
                className={`nav-item ${isActive('/') || location.pathname === startFeedPath ? 'active' : ''}`}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <span>Home</span>
              </Link>

              <Link
                to="/explore"
                className={`nav-item ${isActive('/explore') ? 'active' : ''}`}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="12 2 15 10 12 12 9 10" />
                  <polygon points="22 12 14 15 12 12 14 9" />
                  <polygon points="12 22 9 14 12 12 15 14" />
                  <polygon points="2 12 10 9 12 12 10 15" />
                </svg>
                <span>Explore</span>
              </Link>

              {/* Flex — short-form feeds */}
              <div className="nav-item-group">
                <div className={`nav-item nav-item-expandable ${isActive('/feed') ? 'active' : ''}`}>
                  <Link to="/feed/flex" className="nav-item-link">
                    <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                    <span>Flex</span>
                  </Link>
                  <button
                    className="nav-item-chevron"
                    onClick={() => {
                      setShowFlexSubmenu(v => {
                        if (!v) {
                          setShowListsSubmenu(false);
                          setShowArticleSubmenu(false);
                          setShowBookmarksSubmenu(false); // Tilføjet!
                        }
                        return !v;
                      });
                    }}
                    aria-label={showFlexSubmenu ? 'Collapse Flex feeds' : 'Expand Flex feeds'}
                  >
                    <svg className={`submenu-chevron ${showFlexSubmenu ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>
                {showFlexSubmenu && (
                  <div className="nav-submenu">
                    {enabledFeeds.map(feed => (
                      <Link
                        key={feed.key}
                        to={`/feed/${feed.key}`}
                        className={`nav-subitem ${location.pathname === `/feed/${feed.key}` ? 'active' : ''}`}
                      >
                        <span className="nav-subitem-abbr">{FEED_DISPLAY_CONFIG[feed.key]?.icon || '•'}</span>
                        <span className="nav-subitem-full">{feed.label}</span>
                      </Link>
                    ))}
                    {user && listsWithMembers.length > 0 && (
                      <div className="nav-subitem-lists-wrapper" ref={flexListsRef}>
                        <button
                          className="nav-subitem nav-subitem-lists-toggle"
                            onClick={() => {
                              setShowFlexLists(v => {
                                // No change: nested menus are not mutually exclusive with top-level
                                return !v;
                              });
                            }}
                        >
                          <span className="nav-subitem-abbr">📋</span>
                          <span className="nav-subitem-full">Lists</span>
                          <svg className={`submenu-chevron ${showFlexLists ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                        {showFlexLists && (
                          <div className="nav-submenu nav-submenu-nested">
                            {listsWithMembers.map(list => {
                              const targetPath = `/feed/${encodeURIComponent(list.name)}`;
                              return (
                                <Link
                                  key={list.id}
                                  to={targetPath}
                                  className={`nav-subitem ${location.pathname === targetPath ? 'active' : ''}`}
                                >
                                  <span className="nav-subitem-abbr">📋</span>
                                  <span className="nav-subitem-full">{list.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Link
                to="/trending"
                className={`nav-item ${isActive('/trending') ? 'active' : ''}`}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                </svg>
                <span>Trending</span>
              </Link>

              <Link
                to="/articles/flexus/created"
                className={`nav-item ${isActive('/articles') ? 'active' : ''}`}
                title="Articles"
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>Flux</span>
              </Link>

              <Link
                to="/communities"
                className={`nav-item ${isActive('/communities') ? 'active' : ''}`}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Communities</span>
              </Link>

              {user && (
                <Link
                  to={`/user/${user.username}`}
                  className={`nav-item ${isActive(`/user/${user.username}`) ? 'active' : ''}`}
                >
                  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>Profile</span>
                </Link>
              )}

              {user && (
                <div className="nav-item-group">
                  <div className={`nav-item nav-item-expandable ${isActive('/lists') && !location.pathname.startsWith('/user/') ? 'active' : ''}`}>
                    <Link to="/lists" className="nav-item-link">
                      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                      <span>Lists</span>
                    </Link>
                    <button
                      className="nav-item-chevron"
                      onClick={() => {
                        setShowListsSubmenu(v => {
                          if (!v) {
                            setShowFlexSubmenu(false);
                            setShowArticleSubmenu(false);
                            setShowBookmarksSubmenu(false); // Tilføjet!
                          }
                          return !v;
                        });
                      }}
                      aria-label={showListsSubmenu ? 'Collapse Lists' : 'Expand Lists'}
                    >
                      <svg className={`submenu-chevron ${showListsSubmenu ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                  {showListsSubmenu && (
                    <div className="nav-submenu">
                      {lists.map(list => {
                        const targetPath = `/user/${user.username}/lists/${encodeURIComponent(list.name)}`;
                        return (
                          <Link
                            key={list.id}
                            to={targetPath}
                            className={`nav-subitem ${location.pathname === targetPath ? 'active' : ''}`}
                          >
                            <span className="nav-subitem-abbr">📋</span>
                            <span className="nav-subitem-full">{list.name}</span>
                          </Link>
                        );
                      })}
                      <button
                        type="button"
                        className="nav-subitem nav-subitem-create"
                        onClick={() => setShowCreateListModal(true)}
                      >
                        <span className="nav-subitem-abbr">+</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                        <span className="nav-subitem-full">Create new list</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {user && (
                <div className="nav-item-group">
                  <div className={`nav-item nav-item-expandable ${isActive('/bookmarks') ? 'active' : ''}`}>
                    <Link to="/bookmarks" className="nav-item-link">
                      <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                      </svg>
                      <span>Bookmarks</span>
                    </Link>
                    <button
                      className="nav-item-chevron"
                      onClick={() => {
                        setShowBookmarksSubmenu(v => {
                          if (!v) {
                            setShowFlexSubmenu(false);
                            setShowArticleSubmenu(false);
                            setShowListsSubmenu(false);
                          }
                          return !v;
                        });
                      }}
                      aria-label={showBookmarksSubmenu ? 'Collapse Bookmarks' : 'Expand Bookmarks'}
                    >
                      <svg className={`submenu-chevron ${showBookmarksSubmenu ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                  {showBookmarksSubmenu && (
                    <div className="nav-submenu">
                      {folders.map(folder => {
                        const targetPath = user
                          ? `/user/${user.username}/bookmarks/${encodeURIComponent(folder.name)}`
                          : `/bookmarks/${folder.id}`;
                        return (
                          <Link
                            key={folder.id}
                            to={targetPath}
                            className={`nav-subitem ${location.pathname === targetPath ? 'active' : ''}`}
                          >
                            <span className="nav-subitem-abbr">🔖</span>
                            <span className="nav-subitem-full">{folder.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Article workspace sub-links */}
            {user && (
              <div className="nav-submenu-group">
                <button
                  type="button"
                  className="nav-submenu-toggle"
                  onClick={() => {
                    setShowArticleSubmenu(v => {
                      if (!v) {
                        setShowFlexSubmenu(false);
                        setShowListsSubmenu(false);
                        setShowBookmarksSubmenu(false); // Tilføjet!
                      }
                      return !v;
                    });
                  }}
                  aria-expanded={showArticleSubmenu}
                >
                  <span className="nav-subitem-full">Workspace</span>
                  <span className="nav-subitem-abbr">W</span>
                  <svg className={`submenu-chevron ${showArticleSubmenu ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {showArticleSubmenu && (
                  <div className="nav-submenu">
                    <Link to="/create-article/drafts" className={`nav-subitem ${location.pathname === '/create-article/drafts' ? 'active' : ''}`}><span className="nav-subitem-abbr">D</span><span className="nav-subitem-full">Drafts</span></Link>
                    <Link to="/create-article/templates" className={`nav-subitem ${location.pathname === '/create-article/templates' ? 'active' : ''}`}><span className="nav-subitem-abbr">T</span><span className="nav-subitem-full">Templates</span></Link>
                    <Link to="/create-article/scheduled" className={`nav-subitem ${location.pathname === '/create-article/scheduled' ? 'active' : ''}`}><span className="nav-subitem-abbr">S</span><span className="nav-subitem-full">Scheduled</span></Link>
                    <Link to="/autovoting" className={`nav-subitem ${location.pathname === '/autovoting' ? 'active' : ''}`}><span className="nav-subitem-abbr">A</span><span className="nav-subitem-full">Autovoting</span></Link>
                  </div>
                )}
              </div>
            )}

            {/* Create Post Button */}
            {user && (
              <>
                <div className="create-post-dropdown" ref={createMenuRef}>
                  <button
                    className="create-post-btn"
                    onClick={() => setShowCreateMenu((visible) => !visible)}
                    aria-haspopup="true"
                    aria-expanded={showCreateMenu}
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>Create</span>
                  </button>

                  {showCreateMenu && (
                    <div className="create-post-menu">
                      <Link
                        to="/create-article"
                        className="create-post-menu-item"
                        onClick={() => setShowCreateMenu(false)}
                      >
                        Create article
                      </Link>
                      <button
                        type="button"
                        className="create-post-menu-item"
                        onClick={() => {
                          setShowCreateMenu(false);
                          setShowCreatePost(true);
                        }}
                      >
                        Create Flex
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {expanded && createPortal(
        <div className="sidebar-expanded-backdrop" onClick={() => setExpanded(false)} />,
        document.body
      )}

      {showCreatePost && (
        <CreatePostModal onClose={() => setShowCreatePost(false)} />
      )}

      {showCreateListModal && (
        <CreateListModal onClose={() => setShowCreateListModal(false)} />
      )}
    </>
  );
};

export default Sidebar;